import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Percent } from "lucide-react";
import { cn, formatEur } from "@/lib/utils";
import { getMargenBrutoProyectado } from "@/lib/api";

// C1 (petición Héctor 29-09): Margen Bruto en Proyección — histórico Y
// proyectado en una tabla, con las MISMAS cifras del motor de drivers
// (backend domain/margen.py::margen_bruto_proyectado): ingresos 70x
// efectivos − costes 60x/61x efectivos, aplicando las exclusiones de la
// pestaña "Margen Bruto" del ejercicio. Solo lectura: el margen proyectado
// es CONSECUENCIA de los drivers, no un driver en sí.
export default function MargenBrutoProyeccion({ empresaId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const res = await getMargenBrutoProyectado(empresaId);
      setData(res.data);
    } catch {
      toast.error("Error al cargar el margen bruto proyectado");
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    setLoading(true);
    cargar();
  }, [cargar]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!data) return <p className="text-muted-foreground py-8 text-center">Sin datos.</p>;

  const aniosHistTodos = data.anios_historicos || [];
  const parciales = data.anios_parciales || {};
  // F10d + P14 (Pau oct-2026): un año puede ser ejercicio REAL y proyectado a
  // la vez (año base anterior). Regla fina: si el real está COMPLETO, gana el
  // real (como F10d); si es PARCIAL, se muestran LAS DOS columnas — "2026
  // Hist. (9M)" con lo importado y "2026 Proy." con el año completo que va a
  // la PyG de la proyección. Los meses salen de las fechas del ejercicio.
  const esParcial = (a) => parciales[String(a)] != null;
  const columnas = [
    ...aniosHistTodos.map((a) => ({ anio: a, proy: false })),
    ...(data.anios_proyectados || [])
      .filter((a) => !aniosHistTodos.includes(a) || esParcial(a))
      .map((a) => ({ anio: a, proy: true })),
  ].sort((x, y) => x.anio - y.anio || (x.proy ? 1 : -1));
  const etiqueta = (col) => {
    if (col.proy) return "Proy.";
    const meses = parciales[String(col.anio)];
    return meses != null ? `Hist. (${meses}M)` : "Hist.";
  };
  const valor = (col, campo) =>
    (col.proy ? data.proyectado : data.historico)?.[String(col.anio)]?.[campo];
  const fmtPct = (p) => (p == null ? "—" : `${String(p).replace(".", ",")} %`);

  const filas = [
    { l: "Ingresos (70/71)", c: "ingresos", f: formatEur, hint: "Proyectado: 70x del motor de drivers (la 71x va a 0: partida no driveable)" },
    { l: "Costes (60/61)", c: "costes", f: formatEur, hint: "Proyectado: 60x + 61x del motor de drivers" },
    { l: "Margen Bruto", c: "margen_bruto", f: formatEur, tone: "strong" },
    { l: "Margen Bruto %", c: "margen_bruto_pct", f: fmtPct, tone: "strong" },
  ];

  return (
    <div className="space-y-4" data-testid="margen-proyeccion">
      <div>
        <h3 className="font-heading text-lg font-bold flex items-center gap-2">
          <Percent className="h-5 w-5 text-proy" /> Margen Bruto — histórico y proyectado
        </h3>
        <p className="text-sm text-muted-foreground">
          El margen proyectado es <b>consecuencia de tus drivers</b> de ingresos (70x) y
          compras/variación de existencias (60x/61x) — mismas cifras que el PyG proyectado.
          Las cuentas que entran se eligen en la pestaña <b>Margen Bruto</b> de cada ejercicio
          {data.excluidas?.length > 0 && (
            <> (ahora hay <b>{data.excluidas.length}</b> excluida{data.excluidas.length > 1 ? "s" : ""})</>
          )}.
        </p>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table className="text-sm">
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="min-w-[170px]">Concepto</TableHead>
                {columnas.map((col) => (
                  <TableHead key={`${col.anio}-${col.proy ? "p" : "h"}`}
                    className={cn("text-right whitespace-nowrap", col.proy && "text-primary")}>
                    {col.anio}
                    <span className="block text-[10px] font-normal opacity-70">{etiqueta(col)}</span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas.map((fila) => (
                <TableRow key={fila.c} className={cn(fila.tone === "strong" && "font-semibold bg-primary/5")}>
                  <TableCell title={fila.hint} className="whitespace-nowrap">{fila.l}</TableCell>
                  {columnas.map((col) => (
                    <TableCell key={`${col.anio}-${col.proy ? "p" : "h"}`}
                      className={cn("text-right font-mono tabular-nums whitespace-nowrap", col.proy && "bg-proy")}>
                      {fila.f(valor(col, fila.c))}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Cómo se calcula</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="list-disc pl-5 space-y-1 text-[12px] text-muted-foreground">
            {(data.supuestos || []).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
